<template>
  <div class="ops-dashboard">
    <header class="ops-dashboard__page-head">
      <div class="ops-dashboard__page-head-text">
        <h1 class="ops-dashboard__page-title">상품·재고 현황</h1>
        <!-- <p class="ops-dashboard__page-sub">
          SKU·재고 금액·회전·부족 지표 샘플입니다. 실제 재고와 무관한 데모
          화면입니다.
        </p> -->
      </div>
      <div class="ops-dashboard__page-head-actions">
        <el-button
          type="default"
          size="small"
          icon="el-icon-download"
          class="ops-dashboard__head-btn"
          @click="onDemoExport"
        >
          내보내기
        </el-button>
        <el-tooltip content="데이터를 다시 불러옵니다" placement="bottom">
          <el-button
            type="default"
            size="small"
            icon="el-icon-refresh"
            circle
            class="ops-dashboard__refresh"
            @click="onRefresh"
          />
        </el-tooltip>
      </div>
    </header>

    <section class="ops-dashboard__kpis">
      <el-card
        v-for="item in kpis"
        :key="item.key"
        class="ops-dashboard__kpi-card"
        shadow="hover"
      >
        <p class="ops-dashboard__kpi-label">{{ item.label }}</p>
        <p class="ops-dashboard__kpi-value">{{ item.display }}</p>
        <p
          class="ops-dashboard__kpi-delta"
          :class="item.up ? 'is-up' : 'is-down'"
        >
          {{ item.delta }}
        </p>
      </el-card>
    </section>

    <el-row :gutter="20" class="ops-dashboard__row">
      <el-col :xs="24" :lg="10">
        <el-card class="ops-dashboard__chart-card" shadow="never">
          <div slot="header" class="ops-dashboard__card-head">
            <span>ABC 재고 분류 (금액 비중)</span>
          </div>
          <apexchart
            :key="'abc-' + chartRevision"
            type="donut"
            height="300"
            :options="abcDonutOptions"
            :series="abcDonutSeries"
          />
        </el-card>
      </el-col>
      <el-col :xs="24" :lg="14">
        <el-card class="ops-dashboard__chart-card" shadow="never">
          <div slot="header" class="ops-dashboard__card-head">
            <span>카테고리별 재고 평가액 (백만원)</span>
          </div>
          <apexchart
            :key="'cat-' + chartRevision"
            type="bar"
            height="300"
            :options="categoryBarOptions"
            :series="categoryBarSeries"
          />
        </el-card>
      </el-col>
    </el-row>

    <el-row :gutter="20" class="ops-dashboard__row">
      <el-col :xs="24">
        <el-card class="ops-dashboard__chart-card" shadow="never">
          <div slot="header" class="ops-dashboard__card-head">
            <span>입고·출고 추이 (최근 14일)</span>
          </div>
          <apexchart
            :key="'io-' + chartRevision"
            type="area"
            height="300"
            :options="ioLineOptions"
            :series="ioLineSeries"
          />
        </el-card>
      </el-col>
    </el-row>

    <el-row :gutter="20" class="ops-dashboard__row">
      <el-col :xs="24">
        <el-card class="ops-dashboard__chart-card" shadow="never">
          <div slot="header" class="ops-dashboard__card-head">
            <span>SKU 재고 상위</span>
          </div>
          <el-table
            :data="skuRows"
            stripe
            size="small"
            class="ops-dashboard__table"
          >
            <el-table-column prop="sku" label="SKU" width="120" sortable />
            <el-table-column
              prop="name"
              label="상품명"
              min-width="160"
              sortable
            />
            <el-table-column
              prop="category"
              label="카테고리"
              width="100"
              sortable
            />
            <el-table-column
              prop="qty"
              label="가용재고"
              width="120"
              align="right"
              sortable
            />
            <el-table-column
              prop="safety"
              label="안전재고"
              width="120"
              align="right"
              sortable
            />
            <el-table-column
              prop="days"
              label="회전일(추정)"
              width="130"
              align="right"
              sortable
            />
            <el-table-column
              prop="status"
              label="상태"
              width="112"
              align="center"
              sortable
              :sort-method="sortByStockStatus"
            >
              <template slot-scope="{ row }">
                <el-tag
                  :type="stockStatusTag(row.status)"
                  size="mini"
                  effect="plain"
                  class="ops-dashboard__status-tag"
                >
                  {{ row.statusLabel }}
                </el-tag>
              </template>
            </el-table-column>
          </el-table>
        </el-card>
      </el-col>
    </el-row>
  </div>
</template>

<script>
const PRIMARY = "#254aa5";
const PRIMARY_LIGHT = "#4a90e2";
const MUTED = "#8b95a5";

function buildIoSeries(seed) {
  const labels = Array.from({ length: 14 }, (_, i) => `${i + 1}일`);
  const inb = labels.map((_, i) => {
    const base = 420 + (seed % 40);
    return Math.round(
      base + Math.sin((i / 14) * Math.PI * 2) * 55 + ((i * 3 + seed) % 31)
    );
  });
  const out = inb.map((v, i) =>
    Math.round(
      v * 0.92 + Math.cos((i / 5) * Math.PI) * 28 + ((i * 7 + seed) % 19)
    )
  );
  return { labels, inb, out };
}

function buildMockRows(seed) {
  const cats = ["패션", "리빙", "식품", "뷰티", "가전"];
  const statuses = [
    { key: "ok", label: "정상" },
    { key: "warn", label: "임박" },
    { key: "short", label: "부족" },
  ];
  const names = [
    "프리미엄 코트",
    "유리 보관용기 세트",
    "간편 도시락 6종",
    "선크림 SPF50",
    "무선 청소기",
    "후드 티셔츠",
    "침구 커버",
    "과일 스낵",
  ];
  return names.map((name, i) => {
    const q = 180 + ((seed + i * 17) % 420);
    const s = 100 + ((seed + i * 11) % 80);
    const st = statuses[(seed + i) % 3];
    return {
      sku: `SKU-${1000 + ((seed + i) % 900)}`,
      name,
      category: cats[(seed + i) % cats.length],
      qty: q,
      safety: s,
      days: 12 + ((seed + i * 5) % 18),
      status: st.key,
      statusLabel: st.label,
    };
  });
}

export default {
  name: "OperationsProductInventory",
  data() {
    const seed = 31;
    const io = buildIoSeries(seed);
    return {
      chartRevision: 0,
      kpis: [
        {
          key: "val",
          label: "재고 평가액",
          display: "₩ 12.8억",
          delta: "전주 대비 +2.3%",
          up: true,
        },
        {
          key: "sku",
          label: "보유 SKU",
          display: "3,842개",
          delta: "신규 등록 +18",
          up: true,
        },
        {
          key: "turn",
          label: "평균 재고회전일",
          display: "34일",
          delta: "목표 40일 대비 -6일",
          up: true,
        },
        {
          key: "short",
          label: "부족·임박 SKU",
          display: "47건",
          delta: "전주 대비 -5건",
          up: true,
        },
      ],
      abcDonutSeries: [52, 31, 17],
      abcDonutOptions: {
        chart: { fontFamily: "Pretendard, sans-serif" },
        labels: ["A급", "B급", "C급"],
        colors: [PRIMARY, PRIMARY_LIGHT, "#67c23a"],
        legend: {
          position: "bottom",
          fontSize: "13px",
          labels: { colors: "#505258" },
        },
        plotOptions: {
          pie: {
            donut: {
              size: "68%",
              labels: {
                show: true,
                name: { show: true },
                value: {
                  show: true,
                  formatter: (val) =>
                    val != null && val !== "" ? `${val}%` : "",
                },
                total: {
                  show: false,
                },
              },
            },
          },
        },
        dataLabels: { enabled: false },
        tooltip: {
          theme: "light",
          fillSeriesColor: false,
          y: { formatter: (v) => `${v}%` },
        },
      },
      categoryBarSeries: [
        {
          name: "재고액",
          data: [320, 280, 210, 160, 130, 95],
        },
      ],
      categoryBarOptions: {
        chart: {
          toolbar: { show: false },
          fontFamily: "Pretendard, sans-serif",
        },
        plotOptions: {
          bar: {
            horizontal: true,
            borderRadius: 4,
            barHeight: "55%",
            distributed: true,
          },
        },
        colors: [
          PRIMARY,
          PRIMARY_LIGHT,
          "#67c23a",
          "#e6a23c",
          "#909399",
          "#c0c4cc",
        ],
        dataLabels: {
          enabled: true,
          formatter: (v) => `${v}`,
          style: { fontSize: "11px", colors: ["#fff"] },
        },
        xaxis: {
          categories: ["패션", "리빙", "식품", "뷰티", "가전", "기타"],
          labels: { style: { colors: MUTED } },
        },
        yaxis: {
          labels: { style: { colors: MUTED } },
        },
        grid: { borderColor: "#eef0f3" },
        legend: { show: false },
        tooltip: { y: { formatter: (v) => `${v}백만` } },
      },
      ioLineSeries: [
        { name: "입고(건)", data: io.inb },
        { name: "출고(건)", data: io.out },
      ],
      ioLineOptions: {
        chart: {
          toolbar: { show: false },
          fontFamily: "Pretendard, sans-serif",
        },
        colors: [PRIMARY, "#67c23a"],
        stroke: { curve: "smooth", width: 2 },
        fill: {
          type: "gradient",
          gradient: {
            shadeIntensity: 1,
            opacityFrom: 0.2,
            opacityTo: 0.02,
            stops: [0, 92, 100],
          },
        },
        dataLabels: { enabled: false },
        xaxis: {
          categories: io.labels,
          labels: { style: { colors: MUTED, fontSize: "11px" } },
        },
        yaxis: {
          labels: { style: { colors: MUTED } },
        },
        grid: { borderColor: "#eef0f3", strokeDashArray: 4 },
        legend: { position: "top", horizontalAlign: "right" },
        tooltip: { y: { formatter: (v) => `${v}건` } },
      },
      skuRows: buildMockRows(seed),
    };
  },
  methods: {
    /** 부족 → 임박 → 정상 순(위험도 낮은 쪽이 뒤) */
    sortByStockStatus(a, b) {
      const rank = { short: 0, warn: 1, ok: 2 };
      return (rank[a.status] ?? 9) - (rank[b.status] ?? 9);
    },
    stockStatusTag(key) {
      if (key === "short") {
        return "danger";
      }
      if (key === "warn") {
        return "warning";
      }
      return "success";
    },
    onDemoExport() {
      this.$message.info("데모 환경에서는 내보내기를 지원하지 않습니다.");
    },
    onRefresh() {
      const seed = Date.now() % 997;
      const io = buildIoSeries(seed);
      const v = 11.5 + (seed % 15) / 10;
      const shortN = 35 + (seed % 40);
      this.kpis = [
        {
          key: "val",
          label: "재고 평가액",
          display: `₩ ${v.toFixed(1)}억`,
          delta:
            seed % 2 === 0
              ? `전주 대비 +${(seed % 5) / 10}%`
              : `전주 대비 -${(seed % 4) / 10}%`,
          up: seed % 2 === 0,
        },
        {
          key: "sku",
          label: "보유 SKU",
          display: `${3700 + (seed % 400)}개`,
          delta: `신규 등록 +${10 + (seed % 20)}`,
          up: true,
        },
        {
          key: "turn",
          label: "평균 재고회전일",
          display: `${28 + (seed % 15)}일`,
          delta: "목표 40일 대비",
          up: seed % 3 !== 0,
        },
        {
          key: "short",
          label: "부족·임박 SKU",
          display: `${shortN}건`,
          delta:
            seed % 2 === 0
              ? `전주 대비 -${seed % 8}건`
              : `전주 대비 +${seed % 5}건`,
          up: seed % 2 === 0,
        },
      ];
      this.abcDonutSeries = [48 + (seed % 8), 28 + (seed % 6), 14 + (seed % 5)];
      const baseCats = [320, 280, 210, 160, 130, 95];
      this.categoryBarSeries = [
        {
          name: "재고액",
          data: baseCats.map((c) =>
            Math.max(60, Math.round(c + (seed % 50) - (seed % 30)))
          ),
        },
      ];
      this.ioLineSeries = [
        { name: "입고(건)", data: io.inb },
        { name: "출고(건)", data: io.out },
      ];
      this.ioLineOptions = {
        ...this.ioLineOptions,
        xaxis: {
          ...this.ioLineOptions.xaxis,
          categories: io.labels,
        },
      };
      this.skuRows = buildMockRows(seed);
      this.chartRevision += 1;
      this.$message.success("재고 데이터를 갱신했습니다.");
    },
  },
};
</script>

<style lang="scss" scoped>
.ops-dashboard {
  min-height: 100%;
  width: 100%;
  padding: 24px 32px 48px;
  box-sizing: border-box;
  background: linear-gradient(180deg, #f7f9fc 0%, #fff 32%);
}

.ops-dashboard__page-head {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 22px;
  padding-bottom: 18px;
  border-bottom: 1px solid #ebeef5;
}

.ops-dashboard__page-head-text {
  min-width: 0;
  flex: 1;
}

.ops-dashboard__page-title {
  margin: 0 0 6px;
  font-size: 22px;
  font-weight: 700;
  color: #303133;
  letter-spacing: -0.03em;
}

.ops-dashboard__page-sub {
  margin: 0;
  font-size: 13px;
  line-height: 1.55;
  color: #909399;
}

.ops-dashboard__page-head-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.ops-dashboard__head-btn {
  flex-shrink: 0;
}

.ops-dashboard__refresh {
  flex-shrink: 0;
}

.ops-dashboard__kpis {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 14px;
  margin-bottom: 20px;
}

@media (max-width: 992px) {
  .ops-dashboard__kpis {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 480px) {
  .ops-dashboard__kpis {
    grid-template-columns: 1fr;
  }
}

.ops-dashboard__kpi-card {
  border-radius: 8px;
  border: 1px solid #ebeef5;
}

.ops-dashboard__kpi-label {
  margin: 0 0 6px;
  font-size: 12px;
  color: #909399;
}

.ops-dashboard__kpi-value {
  margin: 0 0 4px;
  font-size: 19px;
  font-weight: 700;
  color: #303133;
  letter-spacing: -0.02em;
}

.ops-dashboard__kpi-delta {
  margin: 0;
  font-size: 12px;
  color: #909399;
}

.ops-dashboard__kpi-delta.is-up {
  color: #67c23a;
}

.ops-dashboard__kpi-delta.is-down {
  color: #f56c6c;
}

.ops-dashboard__row {
  margin-bottom: 20px !important;
}

@media (max-width: 1199px) {
  .ops-dashboard__row > .el-col:not(:last-child) {
    margin-bottom: 20px;
  }
}

.ops-dashboard__chart-card {
  border-radius: 8px;
  border: 1px solid #ebeef5;
  margin-bottom: 0;
}

.ops-dashboard__card-head {
  font-size: 15px;
  font-weight: 600;
  color: #303133;
}

.ops-dashboard__table {
  width: 100%;
}

.ops-dashboard__status-tag {
  border-radius: 4px;
  font-weight: 500;
}

.ops-dashboard__footnote {
  margin: 8px 4px 0;
  font-size: 12px;
  color: #909399;
  text-align: right;
}
</style>
